import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Wand2, MousePointerClick, Rows3, Columns3, Info } from 'lucide-react';
import { SpreadsheetPreview } from './SpreadsheetPreview';
import { autoDetectMapping } from '../utils/parseSpreadsheet';

const getColumnLetter = (index) => {
  if (index === null || index === undefined) return '-';
  let letter = '';
  let i = index;
  while (i >= 0) {
    letter = String.fromCharCode((i % 26) + 65) + letter;
    i = Math.floor(i / 26) - 1;
  }
  return letter;
};

export function MappingStep({ sheetData, mapping, setMapping }) {
  const [autoDetected, setAutoDetected] = useState(false);
  const [clickMode, setClickMode] = useState(null); // null | 'headerRow' | 'nameCol' | 'emailCol' | 'qualStart' | 'qualEnd'

  const handleAutoDetect = useCallback(() => {
    if (!sheetData || sheetData.length === 0) return;
    const detected = autoDetectMapping(sheetData);
    setMapping(detected);
    setAutoDetected(true);
  }, [sheetData, setMapping]);

  // Auto-detect on mount if no mapping set
  useEffect(() => {
    if (sheetData && sheetData.length > 0 && !mapping) {
      handleAutoDetect();
    }
  }, [sheetData, mapping, handleAutoDetect]);

  if (!sheetData || sheetData.length === 0) {
    return <div className="text-sm text-muted-foreground">No data available to map.</div>;
  }

  const headerRow = mapping?.headerRow ?? 0;
  const dataStartRow = mapping?.dataStartRow ?? 1;
  const nameColumn = mapping?.nameColumn ?? 0;
  const emailColumn = mapping?.emailColumn ?? null;
  const qualStartColumn = mapping?.qualStartColumn ?? 1;
  const qualEndColumn = mapping?.qualEndColumn ?? Math.max(...sheetData.slice(0, 5).map(r => (r || []).length), 1) - 1;

  // Calculate max columns from the data
  const maxCols = Math.max(...sheetData.slice(0, 20).map(row => (row || []).length), 0);
  const maxRows = sheetData.length;

  const updateMapping = (key, value) => {
    const parsed = value === '' || value === 'none' ? null : parseInt(value, 10);
    const newMapping = {
      headerRow,
      dataStartRow,
      nameColumn,
      emailColumn,
      qualStartColumn,
      qualEndColumn,
      ...mapping,
      [key]: parsed
    };
    // Auto-adjust: data start row should be after header
    if (key === 'headerRow' && parsed !== null) {
      newMapping.dataStartRow = parsed + 1;
    }
    setMapping(newMapping);
  };

  // Handle clicks on the spreadsheet preview
  const handleRowClick = (rowIndex) => {
    if (clickMode === 'headerRow') {
      updateMapping('headerRow', String(rowIndex));
      setClickMode(null);
    }
  };

  const handleColClick = (colIndex) => {
    if (clickMode === 'nameCol') {
      updateMapping('nameColumn', String(colIndex));
      setClickMode(null);
    } else if (clickMode === 'emailCol') {
      updateMapping('emailColumn', String(colIndex));
      setClickMode(null);
    } else if (clickMode === 'qualStart') {
      updateMapping('qualStartColumn', String(colIndex));
      setClickMode(null);
    } else if (clickMode === 'qualEnd') {
      updateMapping('qualEndColumn', String(colIndex));
      setClickMode(null);
    }
  };

  // Summarize detected data
  const qualCount = qualEndColumn !== null && qualStartColumn !== null
    ? qualEndColumn - qualStartColumn + 1
    : 0;
  const dataRowCount = Math.max(0, sheetData.length - dataStartRow);
  const headerCells = headerRow !== null ? (sheetData[headerRow] || []) : [];
  const headerNames = headerCells
    .slice(qualStartColumn, qualEndColumn + 1)
    .map(v => v != null ? String(v).trim() : '')
    .filter(Boolean);

  // Build column options for selects
  const colOptions = Array.from({ length: maxCols }, (_, i) => ({
    value: String(i),
    label: `${getColumnLetter(i)} — ${headerCells[i] != null ? String(headerCells[i]).trim().substring(0, 20) : '(empty)'}`,
  }));

  // Build row options for selects
  const rowOptions = Array.from({ length: Math.min(maxRows, 20) }, (_, i) => {
    const firstCell = sheetData[i]?.[0];
    const preview = firstCell != null ? String(firstCell).trim().substring(0, 25) : '(empty)';
    return {
      value: String(i),
      label: `Row ${i + 1} — ${preview}`,
    };
  });

  const clickModeLabels = {
    headerRow: 'Click a ROW to set as header row',
    nameCol: 'Click a COLUMN to set as name column',
    emailCol: 'Click a COLUMN to set as email column',
    qualStart: 'Click a COLUMN to set as first qualification column',
    qualEnd: 'Click a COLUMN to set as last qualification column',
  };

  return (
    <div className="space-y-4">
      {/* Top bar: Auto-detect + summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {autoDetected && (
            <Badge variant="secondary" className="text-xs">
              Auto-detected
            </Badge>
          )}
          <span className="text-sm text-muted-foreground">
            {dataRowCount > 0
              ? `${dataRowCount} data rows, ${qualCount} qualification columns`
              : 'Configure mapping below'}
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={handleAutoDetect}>
          <Wand2 className="h-3.5 w-3.5 mr-1.5" />
          Auto-detect
        </Button>
      </div>

      {/* Click mode banner */}
      {clickMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MousePointerClick className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">{clickModeLabels[clickMode]}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setClickMode(null)} className="text-amber-700 hover:text-amber-900 h-7">
            Cancel
          </Button>
        </div>
      )}

      {/* Full-width spreadsheet preview */}
      <SpreadsheetPreview
        data={sheetData}
        maxRows={15}
        highlightHeaderRow={headerRow}
        highlightNameCol={nameColumn}
        highlightEmailCol={emailColumn}
        highlightQualCols={qualStartColumn !== null && qualEndColumn !== null ? [qualStartColumn, qualEndColumn] : null}
        onClickRow={clickMode === 'headerRow' ? handleRowClick : undefined}
        onClickCol={['nameCol', 'emailCol', 'qualStart', 'qualEnd'].includes(clickMode) ? handleColClick : undefined}
        clickMode={clickMode}
      />

      {/* Legend */}
      <div className="flex gap-5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-blue-400" /> Header row
        </span>
        <span className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-green-400" /> Name column
        </span>
        <span className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-emerald-400" /> Email column
        </span>
        <span className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-purple-400" /> Qualification columns
        </span>
      </div>

      {/* Configuration panel — horizontal cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {/* Header Row */}
        <div className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-blue-500" />
              Header Row
            </h4>
            <Button
              variant={clickMode === 'headerRow' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setClickMode(clickMode === 'headerRow' ? null : 'headerRow')}
              className="h-6 w-6 p-0"
              title="Click to select from preview"
            >
              <Rows3 className="h-3 w-3" />
            </Button>
          </div>
          <Select value={String(headerRow)} onValueChange={(v) => updateMapping('headerRow', v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {rowOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Data starts</Label>
            <Select value={String(dataStartRow)} onValueChange={(v) => updateMapping('dataStartRow', v)}>
              <SelectTrigger className="h-7 text-xs flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rowOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    Row {parseInt(opt.value) + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Name Column */}
        <div className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-green-500" />
              Name Column
            </h4>
            <Button
              variant={clickMode === 'nameCol' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setClickMode(clickMode === 'nameCol' ? null : 'nameCol')}
              className="h-6 w-6 p-0"
              title="Click to select from preview"
            >
              <Columns3 className="h-3 w-3" />
            </Button>
          </div>
          <Select value={String(nameColumn)} onValueChange={(v) => updateMapping('nameColumn', v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {colOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Email Column */}
        <div className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
              Email Column
              <span className="text-xs font-normal text-muted-foreground">(optional)</span>
            </h4>
            <Button
              variant={clickMode === 'emailCol' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setClickMode(clickMode === 'emailCol' ? null : 'emailCol')}
              className="h-6 w-6 p-0"
              title="Click to select from preview"
            >
              <Columns3 className="h-3 w-3" />
            </Button>
          </div>
          <Select
            value={emailColumn !== null ? String(emailColumn) : 'none'}
            onValueChange={(v) => updateMapping('emailColumn', v)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" className="text-xs">None</SelectItem>
              {colOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Qualification Columns */}
        <div className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-purple-500" />
              Qualification Columns
            </h4>
            <div className="flex gap-0.5">
              <Button
                variant={clickMode === 'qualStart' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setClickMode(clickMode === 'qualStart' ? null : 'qualStart')}
                className="h-6 px-1.5 text-[10px]"
                title="Click to select first qual column"
              >
                First
              </Button>
              <Button
                variant={clickMode === 'qualEnd' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setClickMode(clickMode === 'qualEnd' ? null : 'qualEnd')}
                className="h-6 px-1.5 text-[10px]"
                title="Click to select last qual column"
              >
                Last
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-muted-foreground">From</Label>
              <Select value={String(qualStartColumn)} onValueChange={(v) => updateMapping('qualStartColumn', v)}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {getColumnLetter(parseInt(opt.value))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">To</Label>
              <Select value={String(qualEndColumn)} onValueChange={(v) => updateMapping('qualEndColumn', v)}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {getColumnLetter(parseInt(opt.value))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Detected qualifications preview */}
      {headerNames.length > 0 && (
        <div className="bg-purple-50/50 border border-purple-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-3.5 w-3.5 text-purple-600" />
            <p className="text-xs font-medium text-purple-800">
              {headerNames.length} qualification{headerNames.length !== 1 ? 's' : ''} detected
              <span className="font-normal text-purple-600 ml-1">
                (columns {getColumnLetter(qualStartColumn)} to {getColumnLetter(qualEndColumn)})
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {headerNames.map((name, i) => (
              <Badge key={i} variant="secondary" className="text-xs bg-purple-100 text-purple-800 font-normal">
                {name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
